import React from "react";
import { Box, Card, Divider, Grid, Typography } from "@mui/material";

import UserRoles from "./UserRoles";
import {
  AutocompleteWithSearch,
  CheckboxField,
  Dropdown,
  InputField,
} from "../../sharedComponents/formFields/FormFields";

// --- Corner Decoration Styles ---
const cornerTagStyle = {
  position: "absolute",
  top: 0,
  left: 0,
  width: 0,
  height: 0,
  borderTop: "50px solid #1976d2", // Corner color
  borderRight: "50px solid transparent",
  zIndex: 10,
};

const cornerTextStyle = {
  position: "absolute",
  top: 8,
  left: 5,
  transform: "rotate(-45deg)",
  color: "#fff",
  fontWeight: 600,
  fontSize: "10px",
  zIndex: 11,
};

// --- Reusable Components ---

const leagues = [
  { id: "09", name: "NBA" },
  { id: "21", name: "G League" },
  { id: "11", name: "BAL" },
];

const userOptions = ["Admin", "Team", "League"];

function AddEditUser({
  userDetails,
  allTeams,
  userRoles,
  handleChange,
  onSaveHandler,
  onCancelAddEdit,
  handleAddRole,
  handleDeleteRow,
  handleRoleChange,
  showAlert,
}) {
  const formattedTeams = allTeams.map((team) => ({
    id: team.teamId,
    name: team.teamName,
  }));

  const formattedUserRoles = userRoles.map((role) => ({
    id: role.roleId,
    name: role.roleName,
  }));

  const validateUniqueRoles = () => {
    const roleLeagues = userDetails.userRole.map(
      (role) => `${role.roleId}-${role.leagueId}`
    );
    return new Set(roleLeagues).size === roleLeagues.length;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const { userType, firstName, lastName, teamId, userRole } = userDetails;

    const isMissingFields =
      [userType, firstName, lastName].includes("") ||
      userRole.some(({ roleId }) => !roleId);

    if (isMissingFields) {
      showAlert("warning", "Please fill in all required fields.");
      return;
    }

    const needsTeam = userType !== "Admin" && teamId <= 0;
    if (needsTeam) {
      showAlert("warning", "Please select team name.");
      return;
    }

    if (!validateUniqueRoles()) {
      showAlert("warning", "Each role and league combination must be unique.");
      return;
    }

    onSaveHandler();
  };

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      {/* Corner Tag */}
      <Box sx={cornerTagStyle}></Box>
      <Box sx={cornerTextStyle}>{userDetails?.userId ? "EDIT" : "ADD"}</Box>

      {/* Main Card */}
      <Card sx={{ p: 4, pt: 3, maxWidth: "100%", mx: "auto", boxShadow: 3 }}>
        <Typography variant="h5" fontWeight={600} mb={2}>
          {userDetails?.userId ? "EDIT" : "Add"} USER
        </Typography>

        <Divider
          sx={{
            mb: 3,
            height: 3,
            borderRadius: 1,
            background: "linear-gradient(to right, #1976d2, #42a5f5)",
          }}
        />

        <Grid container spacing={3}>
          <Dropdown
            label="User Type"
            name="userType"
            value={userDetails.userType}
            handleChange={handleChange}
            placeholder="Select user type"
            options={userOptions}
            required={true}
          />
          <InputField
            label="First Name"
            name="firstName"
            value={userDetails.firstName}
            handleChange={handleChange}
            placeholder="Enter first name"
            required={true}
          />
          <InputField
            label="Last Name"
            name="lastName"
            value={userDetails.lastName}
            handleChange={handleChange}
            placeholder="Enter last name"
            required={true}
          />
          <InputField
            label="Login ID"
            name="loginId"
            value={userDetails.loginId}
            handleChange={handleChange}
            placeholder="Enter login ID"
          />
          <InputField
            label="Email"
            name="email"
            value={userDetails.email}
            handleChange={handleChange}
            placeholder="Enter email"
          />

          {!["Admin", "League"].includes(userDetails.userType) && (
            <InputField
              label="Title"
              name="title"
              value={userDetails.title}
              handleChange={handleChange}
              placeholder="Enter title"
            />
          )}

          {!["Admin"].includes(userDetails.userType) && (
            <AutocompleteWithSearch
              label="Team"
              name="teamId"
              value={userDetails.teamId}
              onChange={handleChange}
              options={formattedTeams}
              placeholder="Select a team"
              required={true}
            />
          )}

          <Dropdown
            label="Rank Level"
            name="rankLevel"
            value={userDetails.rankLevel}
            handleChange={handleChange}
            placeholder="Select Rank"
            options={[0, 1, 2, 3]}
          />

          <CheckboxField
            label="Active User"
            name="active"
            checked={userDetails.active}
            handleChange={handleChange}
            gridSizes={{ lg: 2, md: 3, sm: 6, xs: 12 }}
          />
        </Grid>

        <UserRoles
          userRoles={userDetails.userRole}
          userDetails={userDetails}
          formattedUserRoles={formattedUserRoles}
          leagues={leagues}
          handleAddRole={handleAddRole}
          handleDeleteRow={handleDeleteRow}
          handleRoleChange={handleRoleChange}
          onCancelAddEdit={onCancelAddEdit}
          handleSubmit={handleSubmit}
        />

        <Divider
          sx={{
            my: 4,
            height: 3,
            borderRadius: 1,
            background: "linear-gradient(to right, #1976d2, #42a5f5)",
          }}
        />
      </Card>
    </Box>
  );
}

export default AddEditUser;
